import { useMemo, useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import Page from '@/components/page/Page';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useVDMsList } from '@/api/rpc/hooks/ship-and-debit';
import type { VDM, VDMStatus } from '@erp/rpc';
import { Skeleton } from '@/components/ui/skeleton';

type VDMSearchParams = {
  status?: string[];
  supplierId?: string;
  companyCode?: string;
  page?: number;
  pageSize?: number;
};

const STATUS_OPTIONS: { value: VDMStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'created', label: 'Created' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

function getStatusBadgeVariant(
  status: VDMStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'accepted':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'rejected':
      return 'destructive';
    case 'created':
    default:
      return 'outline';
  }
}

function formatCurrency(value: string | null | undefined): string {
  if (!value) return '$0.00';
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function VDMTableSkeleton() {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">VDM #</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Company</TableHead>
            <TableHead className="text-right">Gross Amount</TableHead>
            <TableHead className="text-right">Total Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-32" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24 ml-auto" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24 ml-auto" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 w-24" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function VDMTable({ vdms }: { vdms: VDM[] }) {
  if (vdms.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center text-muted-foreground">
        No vendor debit memos found
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">VDM #</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Company</TableHead>
            <TableHead className="text-right">Gross Amount</TableHead>
            <TableHead className="text-right">Total Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vdms.map((vdm) => (
            <TableRow key={vdm.id}>
              <TableCell className="font-mono">
                {vdm.gentlyVdmNumber}
                {vdm.vdmNumber && (
                  <span className="text-muted-foreground text-xs block">
                    Ext: {vdm.vdmNumber}
                  </span>
                )}
              </TableCell>
              <TableCell>{vdm.supplier?.name ?? '-'}</TableCell>
              <TableCell>
                <span className="font-mono text-sm">{vdm.companyCode}</span>
                {vdm.divisionCode && (
                  <span className="text-muted-foreground text-xs block">
                    Div: {vdm.divisionCode}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(vdm.grossAmount)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(vdm.totalAmount)}
              </TableCell>
              <TableCell>
                <Badge variant={getStatusBadgeVariant(vdm.status)}>
                  {vdm.status.charAt(0).toUpperCase() + vdm.status.slice(1)}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(vdm.createdAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function VDMsPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({
    from: '/authenticated/ship-and-debit/vdm',
  }) as VDMSearchParams;

  // Derive pagination state from URL search params
  const paginationState = useMemo(
    () => ({
      pageIndex: (searchParams.page ?? 1) - 1,
      pageSize: searchParams.pageSize ?? 25,
    }),
    [searchParams.page, searchParams.pageSize],
  );

  // Helper to update URL search params
  const updateSearchParams = useCallback(
    (updates: Partial<VDMSearchParams>, resetPage = false) => {
      const newSearch = {
        ...searchParams,
        ...updates,
        page: resetPage ? undefined : (updates.page ?? searchParams.page),
      };
      navigate({
        to: '/ship-and-debit/vdm',
        search: newSearch,
        replace: true,
      });
    },
    [navigate, searchParams],
  );

  // Build query filters from URL params
  const queryFilters = useMemo(() => {
    return {
      status:
        searchParams.status && searchParams.status.length > 0
          ? (searchParams.status as VDMStatus[])
          : undefined,
      supplierId: searchParams.supplierId || undefined,
      companyCode: searchParams.companyCode || undefined,
      limit: paginationState.pageSize,
      offset: paginationState.pageIndex * paginationState.pageSize,
    };
  }, [searchParams, paginationState]);

  const { vdms, pagination, isLoading } = useVDMsList(queryFilters);

  const rowCount = pagination?.total ?? 0;
  const pageCount =
    rowCount > 0 ? Math.ceil(rowCount / paginationState.pageSize) : 0;

  // Get current status filter value for select
  const currentStatusFilter =
    searchParams.status && searchParams.status.length === 1
      ? searchParams.status[0]
      : 'all';

  function handleStatusChange(value: string) {
    if (value === 'all') {
      updateSearchParams({ status: undefined }, true);
    } else {
      updateSearchParams({ status: [value] }, true);
    }
  }

  function handlePreviousPage() {
    if (paginationState.pageIndex > 0) {
      updateSearchParams({ page: paginationState.pageIndex });
    }
  }

  function handleNextPage() {
    if (paginationState.pageIndex < pageCount - 1) {
      updateSearchParams({ page: paginationState.pageIndex + 2 });
    }
  }

  return (
    <Page title="Vendor Debit Memos">
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Select
              value={currentStatusFilter}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Show active filters count */}
          {searchParams.status && searchParams.status.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateSearchParams({ status: undefined }, true)}
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Table */}
        {isLoading ? <VDMTableSkeleton /> : <VDMTable vdms={vdms} />}

        {/* Pagination */}
        {!isLoading && rowCount > 0 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {paginationState.pageIndex * paginationState.pageSize + 1}{' '}
              to{' '}
              {Math.min(
                (paginationState.pageIndex + 1) * paginationState.pageSize,
                rowCount,
              )}{' '}
              of {rowCount} VDMs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={paginationState.pageIndex === 0}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {paginationState.pageIndex + 1} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={paginationState.pageIndex >= pageCount - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}
